import { apiClient } from '../api/apiClient';

export interface EmailResult {
  success: boolean;
  messageId?: string;
  error?: any;
  message?: string;
  alreadyVerified?: boolean;
  registrationSource?: 'web' | 'mobile';
}

class EmailService {
  /**
   * Resend verification email (generates new token on server)
   * @param email Recipient email address
   */
  async sendEmailVerification(
    email: string
  ): Promise<EmailResult> {
    // Validate email input
    if (!email || typeof email !== 'string' || email.trim().length === 0) {
      return {
        success: false,
        error: 'Email is required',
        message: 'Please provide a valid email address.'
      };
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedEmail = email.trim().toLowerCase();
    
    if (!emailRegex.test(normalizedEmail)) {
      return {
        success: false,
        error: 'Invalid email format',
        message: 'Please provide a valid email address.'
      };
    }

    try {
      const response = await apiClient.post('/auth/resend-verification', {
        email: normalizedEmail
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Verification email sent successfully'
        };
      } else {
        throw new Error(response.data?.message || 'Unknown error from API');
      }
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[EmailService] Error sending verification email:', error);
      }
      
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || 'Failed to send verification email';
      
      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Verify an email using a token
   * @param token Verification token
   * @param userType Optional: 'affiliate' to use affiliate endpoint, otherwise uses standard auth endpoint
   */
  async verifyEmail(token: string, userType?: 'affiliate'): Promise<EmailResult> {
    // Validate token format
    if (!token || typeof token !== 'string' || token.trim().length === 0) {
      return {
        success: false,
        error: 'Invalid token',
        message: 'Verification token is required.'
      };
    }

    // Validate token format (40 char hex string)
    const tokenRegex = /^[a-f0-9]{40}$/i;
    if (!tokenRegex.test(token.trim())) {
      return {
        success: false,
        error: 'Invalid token format',
        message: 'The verification token format is invalid. Please use the link from your email.'
      };
    }

    try {
      const sanitizedToken = token.trim();
      // Auth routes are mounted at /api/v1/affiliate/auth (not /api/v1/affiliate — that stack is JWT-protected)
      const endpoint = userType === 'affiliate' 
        ? `/v1/affiliate/auth/verify-email/${sanitizedToken}`
        : `/auth/verify-email?token=${sanitizedToken}`;
      
      const response = await apiClient.get(endpoint);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Email verified successfully',
          alreadyVerified: response.data.alreadyVerified || false,
          registrationSource: response.data.registrationSource
        };
      } else {
        throw new Error(response.data?.message || 'Unknown error verifying email');
      }
    } catch (error: any) {
      // Don't log sensitive token info in production
      if (process.env.NODE_ENV !== 'production') {
        console.error('[EmailService] Error verifying email:', error);
      }
      
      // Extract user-friendly error message
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || 'Failed to verify email';
      
      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Resend verification email for affiliate
   * @param email Recipient email address
   * @param userType Optional: 'affiliate' to use affiliate endpoint
   */
  async sendAffiliateVerification(email: string): Promise<EmailResult> {
    // Validate email input
    if (!email || typeof email !== 'string' || email.trim().length === 0) {
      return {
        success: false,
        error: 'Email is required',
        message: 'Please provide a valid email address.'
      };
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedEmail = email.trim().toLowerCase();
    
    if (!emailRegex.test(normalizedEmail)) {
      return {
        success: false,
        error: 'Invalid email format',
        message: 'Please provide a valid email address.'
      };
    }

    try {
      const response = await apiClient.post('/v1/affiliate/auth/resend-verification', {
        email: normalizedEmail
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message || 'Verification email sent successfully'
        };
      } else {
        throw new Error(response.data?.message || 'Unknown error from API');
      }
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[EmailService] Error sending affiliate verification email:', error);
      }
      
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || 'Failed to send verification email';
      
      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }
}

// Export a singleton instance
const emailService = new EmailService();
export default emailService;

